import type { TOC } from '@ember/component/template-only'
import type BookLibraryCollectionIndexRoute from 'teacher-ui-client/subscription-type/book-library/collection/index/route'
import type CollectionIndexController from 'teacher-ui-client/subscription-type/book-library/collection/index/controller'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import { t } from 'ember-intl'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { BookLibraryCollectionInlineEdit } from 'teacher-ui-client/components/book-library/collection-inline-edit'
import { CarouselScrollComponent } from 'teacher-ui-client/components/base/carousel-scroll'
import { BookLibraryCollectionCard } from 'teacher-ui-client/components/book-library/collection-card'
import { BookLibraryDeleteCollectionModal } from 'teacher-ui-client/components/book-library/delete-collection-modal'
import { placeholderImage } from 'teacher-ui-client/helpers/placeholder-image'
import { fn } from '@ember/helper'
import RouteTemplate from 'ember-route-template'

interface Signature {
  Args: {
    model: ModelFor<BookLibraryCollectionIndexRoute>
    controller: CollectionIndexController
  }
}
export const BookLibraryCollectionIndexPage: TOC<Signature> = <template>
  <ContentTile>
    <h3
      class="bg-oceany-blue-50 text-dusty-black-500 flex w-full items-center py-3 pl-8 italic"
      data-test-result-panel-header
    >
      <span class="mr-2">
        {{t "bookLibrary.pages.collections.name"}}
      </span>
      {{#if @model.bookCollectionsLoader.isFinished}}
        <span class="text-xl font-normal uppercase">
          ({{t "bookLibrary.collections.count" count=@controller.collections.length}},
          {{t "bookLibrary.bookCount" count=@controller.bookCount}})
        </span>
      {{/if}}
    </h3>
    <div class="mt-4 mb-8" data-test-result-panel-body>
      {{#if @model.bookCollectionsLoader.isRunning}}
        <FidgetSpinnerWaveComponent @centered={{true}} />
      {{else}}
        <div class="mr-4 text-right">
          <div class="inline-block">
            <BookLibraryCollectionInlineEdit
              @prefixIcon="phosphor-plus-bold"
              @buttonIcon="phosphor-folder-plus-fill"
              @onSuccess={{@controller.onCollectionCreateSuccess}}
              @onFail={{@controller.onCollectionCreateFail}}
            >
              <span data-test-create-collection-text>
                {{t "bookLibrary.components.collectionSelect.createLabel"}}
              </span>
            </BookLibraryCollectionInlineEdit>
          </div>
        </div>
        <CarouselScrollComponent @navButtonsOnHover={{true}}>
          <div class="text-center">
            {{#each @controller.collectionsView.firstRow as |bookCollection|}}
              <BookLibraryCollectionCard
                class="mx-4 whitespace-normal"
                data-test-book-library-collection-card
                @bookCollection={{bookCollection}}
                @deleteCollectionAction={{fn @controller.openDeleteCollectionModal bookCollection}}
              />
              {{! A hack to produce a load event, that ensures carousel resizes when this card is inserted }}
              {{#unless bookCollection.coverPreviewImg}}
                <img src={{placeholderImage width=0 height=0}} alt="" role="none" class="hidden" />
              {{/unless}}
            {{/each}}
          </div>
          {{#if @controller.collectionsView.secondRow}}
            <div class="mt-8 text-center">
              {{#each @controller.collectionsView.secondRow as |bookCollection|}}
                <BookLibraryCollectionCard
                  class="mx-4 whitespace-normal"
                  data-test-book-library-collection-card
                  @bookCollection={{bookCollection}}
                  @deleteCollectionAction={{fn @controller.openDeleteCollectionModal bookCollection}}
                />
                {{#unless bookCollection.coverPreviewImg}}
                  <img src={{placeholderImage width=0 height=0}} alt="" role="none" class="hidden" />
                {{/unless}}
              {{/each}}
            </div>
          {{/if}}
        </CarouselScrollComponent>
      {{/if}}
    </div>
  </ContentTile>
  {{#if @controller.deleteCandidate}}
    <BookLibraryDeleteCollectionModal
      @showModal={{@controller.isShowingDeleteModal}}
      @deleteCandidate={{@controller.deleteCandidate}}
      @onDeleteCollectionCancel={{@controller.onDeleteCollectionCancel}}
      @onDeleteCollectionSubmit={{fn @controller.onDeleteCollectionSubmit @controller.deleteCandidate}}
    />
  {{/if}}
</template>

export default RouteTemplate(BookLibraryCollectionIndexPage)
